import { useCallback, useMemo, useState } from 'react';
import { ChatLanguage, normalizeLanguage } from '../i18n';

const STORAGE_KEY = 'xenio.chat.language';

function readStoredLanguage(): ChatLanguage | undefined {
   try {
      return normalizeLanguage(window.localStorage.getItem(STORAGE_KEY));
   } catch {
      return undefined; // storage blocked (private mode, sandboxed iframe, ...)
   }
}

function writeStoredLanguage(language: ChatLanguage): void {
   try {
      window.localStorage.setItem(STORAGE_KEY, language);
   } catch {
      // storage blocked: the choice still applies for this page view
   }
}

/**
 * Browser language reduced to the supported primary tag. Anything that is not
 * Greek maps to English (the only two UI languages).
 */
export function detectBrowserLanguage(): ChatLanguage {
   if (typeof navigator === 'undefined') return 'en';
   const candidates = [...(navigator.languages ?? []), navigator.language];
   for (const tag of candidates) {
      const primary = typeof tag === 'string' ? tag.trim().toLowerCase().split(/[-_]/)[0] : '';
      if (primary) return normalizeLanguage(primary) ?? 'en';
   }
   return 'en';
}

/**
 * UI language + the `language` sent with messages.
 *
 * - UI language: explicit toggle > server `preferredLanguage` > browser.
 * - Sent `language`: ONLY the guest's explicit toggle choice. The server treats it as an explicit
 *   preference that wins over the language of the message, so the browser locale must never be sent
 *   as `language` (a Greek-locale browser asking in English would otherwise be locked to Greek).
 *   Without a toggle the assistant replies in the language the guest writes in.
 */
export const useChatLanguage = (serverPreferredLanguage?: string) => {
   const [explicitLanguage, setExplicitLanguage] = useState<ChatLanguage | undefined>(() => readStoredLanguage());
   const browserLanguage = useMemo(() => detectBrowserLanguage(), []);

   const uiLanguage: ChatLanguage = explicitLanguage ?? normalizeLanguage(serverPreferredLanguage) ?? browserLanguage;

   const setLanguage = useCallback((language: ChatLanguage) => {
      setExplicitLanguage(language);
      writeStoredLanguage(language);
   }, []);

   const getLanguageHint = useCallback(
      // `isFirstMessage` is kept for call-site compatibility; the browser locale is deliberately not sent.
      (_isFirstMessage: boolean): ChatLanguage | undefined => explicitLanguage,
      [explicitLanguage]
   );

   return { uiLanguage, explicitLanguage, setLanguage, getLanguageHint };
};
